import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  BackendErrorCode,
  Channel,
  extractBackendErrorMessage,
  getBackendErrorCode,
  getBackendErrorStatus,
  postSubscription,
} from '@/services/hsm-backend';
import { markMigrationPending } from '@/services/recent-companies';

export interface ActivateSubscriptionProps {
  companyId: string;
  /** Company channels, used to summarize the WhatsApp lines in the empty state. */
  channels: Channel[];
  refetch: () => Promise<void>;
}

const lineWord = (count: number) => (count === 1 ? 'línea' : 'líneas');

interface ActivateSubscriptionFormValues {
  renewalDate: string;
}

/**
 * Screen shown by `CompanyShell` when a company exists but has no HSM
 * subscription (`GET /company` → 200 with `subscription: null`). Two steps:
 * an empty-state CTA and the activation form. On success it activates the
 * subscription via `POST /company/{id}/subscription` and `refetch()`s the
 * shell — which, now with a subscription, drops into the operative detail.
 *
 * @param companyId Company to activate (no subscription yet).
 * @param refetch   Shell's getCompany handle, called after a successful POST.
 */
export const ActivateSubscription = ({
  companyId,
  channels,
  refetch,
}: ActivateSubscriptionProps) => {
  const whatsappApiCount = channels.filter(
    (channel) => channel.channelType === 'WHATSAPP_API'
  ).length;
  const whatsappBusinessCount = channels.filter(
    (channel) => channel.channelType === 'WHATSAPP_BUSINESS'
  ).length;

  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ActivateSubscriptionFormValues>({
    defaultValues: { renewalDate: '' },
  });

  const onSubmit = async (values: ActivateSubscriptionFormValues) => {
    try {
      await postSubscription(
        companyId,
        values.renewalDate ? { renewalDate: values.renewalDate } : {}
      );
      enqueueSnackbar('Suscripción activada', { variant: 'success' });
      await refetch();
    } catch (error) {
      // 409 — the company is still active in the legacy app (TSA). Distinct
      // from the "already has a subscription" 409 below: nothing changed here,
      // so don't refetch — the operator must migrate it instead.
      if (
        getBackendErrorCode(error) ===
        BackendErrorCode.LEGACY_SUBSCRIPTION_ACTIVE
      ) {
        // No GET-time signal exists for this, so persist what the attempt
        // revealed: the recent-companies chip shows "Migración pendiente".
        markMigrationPending(companyId);
        enqueueSnackbar(
          'La empresa tiene una suscripción activa en la app legacy. Hay que migrarla en vez de darla de alta acá.',
          { variant: 'warning' }
        );
        return;
      }
      // 409 — the company already has a subscription (e.g. created in another
      // tab): refetch so the shell catches up, no hard error.
      if (getBackendErrorStatus(error) === 409) {
        enqueueSnackbar('La empresa ya tiene una suscripción', {
          variant: 'info',
        });
        await refetch();
        return;
      }
      enqueueSnackbar(
        `No se pudo activar la suscripción: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    }
  };

  if (!showForm) {
    return (
      <Box
        sx={{ maxWidth: 560, mx: 'auto', px: 3, py: 8, textAlign: 'center' }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.light',
              color: 'primary.dark',
            }}
          >
            <CreditCardOutlinedIcon />
          </Box>
          <Typography variant="h5" component="h1" fontWeight={700}>
            Esta empresa no tiene suscripción HSM
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta empresa todavía no tiene una suscripción para enviar HSM.
            Cuenta con {whatsappApiCount} {lineWord(whatsappApiCount)} de
            WhatsApp API y {whatsappBusinessCount}{' '}
            {lineWord(whatsappBusinessCount)} de WhatsApp Business.
          </Typography>
          <Chip
            label={companyId}
            variant="outlined"
            sx={{ fontFamily: 'ui-monospace, monospace' }}
          />
          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            <Button variant="text" onClick={() => navigate('/')}>
              Cambiar empresa
            </Button>
            <Button variant="contained" onClick={() => setShowForm(true)}>
              Activar suscripción
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', px: 3, py: 4 }}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        Activar suscripción HSM
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Crea la suscripción HSM de esta empresa para empezar a gestionar líneas
        y consumo.
      </Typography>
      <Card>
        <CardContent>
          <Box
            component="form"
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <Typography variant="h6">Nueva suscripción</Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Stack spacing={2}>
              <Alert severity="info">
                Al activar, la empresa queda con suscripción{' '}
                <strong>activa</strong> y se habilitan las tabs de{' '}
                <strong>Plan</strong>, <strong>Paquete</strong> y{' '}
                <strong>Crédito</strong>. Después podés contratar un plan.
              </Alert>
              <TextField
                label="Fecha de renovación (opcional)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={
                  errors.renewalDate?.message ??
                  'Si la dejás vacía, se usa la fecha por defecto (día 15 de cada mes).'
                }
                error={Boolean(errors.renewalDate)}
                {...register('renewalDate', {
                  validate: (value) => {
                    // Optional: an empty value falls back to the backend default.
                    if (!value) {
                      return true;
                    }
                    // Parse as a local date (not UTC midnight) and compare at
                    // day granularity so "today" in AR (UTC-3) isn't flagged.
                    const picked = new Date(`${value}T00:00:00`);

                    if (Number.isNaN(picked.getTime())) {
                      return 'Fecha inválida';
                    }
                    const today = new Date();

                    today.setHours(0, 0, 0, 0);
                    if (picked < today) {
                      return 'No puede ser una fecha pasada';
                    }
                    return true;
                  },
                })}
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ mt: 3 }}
            >
              <Button
                variant="text"
                onClick={() => setShowForm(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                Activar suscripción
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
